import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {List} from 'immutable';
import {getModelForResourceType} from 'resources/modelRegistry';
import classnames from 'classnames';
import './school-lookup-modal.scss';

import {Anchor, Text, Radio, Button, Label, Checkbox, CheckboxChip} from '@albert-io/atomic';

const SchoolSearchResults = ({
  results,
  selected,
  setSelected,
  onSubmit,
  shouldShowHelpText,
  errorMessage,
  multiSelect,
  hasExistingRecord,
  onlyShowChips = false
}) => {
  const [showHelpText, setShowHelpText] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowHelpText(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  function isSelectedSchool(schoolId) {
    if (!multiSelect) {
      return selected?.getId?.() === schoolId;
    }
    return selected.map((school) => school.getId()).includes(schoolId);
  }

  return (
    <div className='school-lookup-modal__results-container mt-x2'>
      <div className='school-lookup-modal__results-container__result-header-row'>
        <div className='ml-x5'>
          <Text bold size='m'>
            School
          </Text>
        </div>
        <div className='text-right'>
          <Text bold size='m'>
            District
          </Text>
        </div>
      </div>
      <div
        className='school-lookup-modal__results-container__results my-x0 px-x0'
        role='radiogroup'
        aria-label='school options'
      >
        {!onlyShowChips &&
          results.map((item) => {
            const schoolAddress = item.getFullAddress().split(', ');
            const schoolStreetAddress = schoolAddress[0];
            const schoolCity = schoolAddress[1];
            const schoolState = schoolAddress[2];
            const schoolPostalCode = schoolAddress[3];
            return (
              <Label
                className={classnames('school-lookup-modal__results-container__result-row', {
                  'school-lookup-modal__results-container__result-row--selected': isSelectedSchool(
                    item.getId()
                  ),
                  'school-lookup-modal__results-container__result-row--disabled':
                    hasExistingRecord?.(item.getId())
                })}
              >
                <div className='flex items-center text-left'>
                  {multiSelect ? (
                    <Checkbox
                      checked={isSelectedSchool(item.getId())}
                      className='mr-x2'
                      onChange={() => setSelected(item)}
                      disabled={hasExistingRecord?.(item.getId())}
                      name='school'
                    />
                  ) : (
                    <Radio
                      checked={isSelectedSchool(item.getId())}
                      className='mr-x2'
                      onChange={() => setSelected(item)}
                      disabled={hasExistingRecord?.(item.getId())}
                      name='school'
                    />
                  )}
                  <div className='flex flex-col'>
                    <Text
                      bold
                      size='m'
                      className={classnames(
                        'school-lookup-modal__results-container__result-row__school-name',
                        {
                          'u-color_slate-400': hasExistingRecord?.(item.getId())
                        }
                      )}
                    >
                      {item.getName()}
                    </Text>
                    <Text
                      color='secondary'
                      size='xs'
                      className={classnames({
                        'u-color_slate-400': hasExistingRecord?.(item.getId())
                      })}
                    >
                      {schoolStreetAddress}
                    </Text>
                    <Text
                      color='secondary'
                      size='xs'
                      className={classnames({
                        'u-color_slate-400': hasExistingRecord?.(item.getId())
                      })}
                    >
                      {schoolCity}, {schoolState} {schoolPostalCode}
                    </Text>
                    {hasExistingRecord?.(item.getId()) && (
                      <Text className='school-lookup-modal__result-already-saved-text u-color_yellow-700 italic'>
                        This school is already associated with this user.
                      </Text>
                    )}
                  </div>
                </div>
                <div className='flex items-center justify-end text-right'>
                  <Text
                    color='secondary'
                    size='s'
                    className={classnames({
                      'u-color_slate-300': hasExistingRecord?.(item.getId())
                    })}
                  >
                    {item.getIn(['district', 'name'], '')}
                  </Text>
                </div>
              </Label>
            );
          })}
      </div>
      {!!errorMessage && errorMessage}
      {multiSelect && <SchoolSearchMultiDisplay items={selected} setSelected={setSelected} />}
      <Button
        className='mt-x4 w-full'
        disabled={(multiSelect ? !selected.length : !selected) || errorMessage}
        onClick={onSubmit}
      >
        Continue
      </Button>
      {showHelpText && shouldShowHelpText ? (
        <div className='pt-x3 text-center'>
          <Text>
            Not finding your school?{' '}
            <span className='font-bold' style={{marginLeft: 4}}>
              <Anchor
                underlined
                target='_blank'
                rel='noopener noreferrer'
                href='https://albert-licenses.typeform.com/to/QtQ9gHSv'
              >
                Contact support
              </Anchor>
            </span>
          </Text>
        </div>
      ) : null}
    </div>
  );
};

SchoolSearchResults.propTypes = {
  results: PropTypes.instanceOf(List),
  selected: PropTypes.oneOfType([
    PropTypes.instanceOf(getModelForResourceType('school_v5')),
    PropTypes.arrayOf(PropTypes.instanceOf(getModelForResourceType('school_v5')))
  ]),
  onlyShowChips: PropTypes.bool,
  setSelected: PropTypes.func,
  onSubmit: PropTypes.func,
  shouldShowHelpText: PropTypes.bool,
  errorMessage: PropTypes.node,
  multiSelect: PropTypes.bool,
  hasExistingRecord: PropTypes.func
};

export default SchoolSearchResults;

const SchoolSearchMultiDisplay = ({items, setSelected}) => (
  <div className='mt-x2'>
    {items.map((item) => (
      <CheckboxChip
        color='brand'
        icon='times'
        key={item.getId()}
        onChange={() => setSelected(item)}
      >
        {item.getName()}
      </CheckboxChip>
    ))}
  </div>
);

SchoolSearchMultiDisplay.propTypes = {
  items: PropTypes.arrayOf(PropTypes.instanceOf(getModelForResourceType('school_v5'))),
  setSelected: PropTypes.func
};
